"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Great_Vibes, Cormorant_Garamond } from "next/font/google";
import GuestbookCards from "@/app/components/GuestbookCards";
import GuestbookSignaturePad from "@/app/components/GuestbookSignaturePad";
import SessionBackLink from "@/app/components/SessionBackLink";
import {
  GUESTBOOK_MESSAGE_MAX_LENGTH,
  GUESTBOOK_NAME_MAX_LENGTH,
} from "@/app/lib/guestbookConfig";
import { GuestbookEntry } from "@/app/lib/weddingPortal";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

const cormorant = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

type StatusTone = "info" | "error";

type GuestbookResponse = GuestbookEntry | { error: string };

export default function GuestbookPage() {
  const entriesSectionRef = useRef<HTMLElement | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const [typedName, setTypedName] = useState("");
  const [message, setMessage] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [statusText, setStatusText] = useState("");
  const [statusTone, setStatusTone] = useState<StatusTone>("info");
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastSavedEntry, setLastSavedEntry] = useState<GuestbookEntry | null>(null);
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    const res = await fetch("/api/guestbook", { cache: "no-store" });
    const json = (await res.json()) as GuestbookEntry[] | { error: string };

    if (!res.ok || !Array.isArray(json)) {
      throw new Error("Unable to load guestbook messages.");
    }

    return json;
  }, []);

  useEffect(() => {
    let active = true;

    async function loadInitialEntries() {
      try {
        const loadedEntries = await fetchEntries();
        if (active) {
          setEntries(loadedEntries);
          setStatusText("");
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setStatusTone("error");
          setStatusText("Sorry, we couldn't load the guestbook messages. Please try again in a moment.");
        }
      } finally {
        if (active) {
          setIsLoadingEntries(false);
        }
      }
    }

    loadInitialEntries();

    return () => {
      active = false;
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, [fetchEntries]);

  function showStatus(messageText: string, tone: StatusTone = "info") {
    setStatusTone(tone);
    setStatusText(messageText);
  }

  function clearStatus() {
    if (statusText) {
      setStatusText("");
    }
  }

  function validateForm() {
    const trimmedName = typedName.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName) {
      showStatus("Please add your name before signing the guestbook.", "error");
      return null;
    }

    if (!trimmedMessage) {
      showStatus("Please write a message before signing the guestbook.", "error");
      return null;
    }

    if (trimmedName.length > GUESTBOOK_NAME_MAX_LENGTH) {
      showStatus(`Please keep your name under ${GUESTBOOK_NAME_MAX_LENGTH} characters.`, "error");
      return null;
    }

    if (trimmedMessage.length > GUESTBOOK_MESSAGE_MAX_LENGTH) {
      showStatus(`Please keep your message under ${GUESTBOOK_MESSAGE_MAX_LENGTH} characters.`, "error");
      return null;
    }

    return {
      typedName: trimmedName,
      message: trimmedMessage,
      signatureDataUrl,
    };
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (isSubmitting) return;

    const validated = validateForm();
    if (!validated) return;

    setIsSubmitting(true);
    showStatus("Submitting your message...", "info");

    try {
      const res = await fetch("/api/guestbook", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(validated),
      });

      const json = (await res.json().catch(() => null)) as GuestbookResponse | null;
      if (!res.ok || !json || "error" in json) {
        showStatus(
          json && "error" in json
            ? json.error
            : "Sorry, we couldn't save your message. Please try again in a moment.",
          "error"
        );
        return;
      }

      setEntries((prev) => [json, ...prev.filter((entry) => entry.id !== json.id)]);
      setLastSavedEntry(json);
      setTypedName("");
      setMessage("");
      setSignatureDataUrl(null);
      setStatusText("");
      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      showStatus("Sorry, we couldn't save your message. Please try again in a moment.", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function scrollToNewestMessage(entryId: string) {
    if (highlightTimerRef.current) {
      window.clearTimeout(highlightTimerRef.current);
    }

    window.requestAnimationFrame(() => {
      entriesSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setHighlightedEntryId(entryId);

      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightedEntryId(null);
      }, 4500);
    });
  }

  async function closeSuccessDialog() {
    setShowSuccessDialog(false);

    try {
      const freshEntries = await fetchEntries();
      setEntries(freshEntries);
      const entryId = lastSavedEntry?.id ?? freshEntries[0]?.id;
      if (entryId) {
        scrollToNewestMessage(entryId);
      }
    } catch (error) {
      console.error(error);
      if (lastSavedEntry?.id) {
        scrollToNewestMessage(lastSavedEntry.id);
      }
    }
  }

  const messageCharactersRemaining = GUESTBOOK_MESSAGE_MAX_LENGTH - message.length;
  const formDisabled = isSubmitting;

  return (
    <main className={`min-h-screen bg-[#f6f4ef] px-4 py-6 text-slate-800 sm:px-6 lg:px-8 ${cormorant.className}`}>
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between gap-4">
          <SessionBackLink />

          <h1
            className={`${greatVibes.className} wedding-gold-halo text-5xl text-[#1f4e79] sm:text-6xl`}
          >
            Guestbook
          </h1>

          <div className="w-20" />
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="rounded-[1.75rem] border border-[#decf9b] bg-white px-6 py-7 shadow-sm">
            <p className="text-[11px] uppercase tracking-[.24em] text-[#8c8c86]">
              Leave a Message
            </p>
            <h2 className="mt-3 text-4xl font-semibold text-[#1f3f6d]">
              Sign John &amp; Maxine&apos;s guestbook
            </h2>
            <p className="mt-4 text-base leading-8 text-[#686861]">
              Write your message, add your name, and optionally sign it with your finger or Apple Pencil.
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-[#1f3f6d]" htmlFor="guestbook-message">
                  Your message
                </label>
                <textarea
                  id="guestbook-message"
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    clearStatus();
                  }}
                  rows={5}
                  maxLength={GUESTBOOK_MESSAGE_MAX_LENGTH}
                  disabled={formDisabled}
                  required
                  aria-invalid={statusTone === "error" && !message.trim()}
                  className="min-h-40 w-full rounded-[1.25rem] border border-[#d9cfb3] bg-[#fffdfa] px-4 py-3 text-base leading-7 outline-none focus:border-[#caa23a] disabled:cursor-not-allowed disabled:opacity-70"
                  placeholder="Write something lovely for the couple..."
                />
                <p className="mt-2 text-right text-xs uppercase tracking-[.14em] text-[#8c8c86]">
                  {messageCharactersRemaining} characters left
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#1f3f6d]" htmlFor="guestbook-name">
                  Your name
                </label>
                <input
                  id="guestbook-name"
                  type="text"
                  value={typedName}
                  onChange={(e) => {
                    setTypedName(e.target.value);
                    clearStatus();
                  }}
                  maxLength={GUESTBOOK_NAME_MAX_LENGTH}
                  disabled={formDisabled}
                  required
                  autoComplete="name"
                  enterKeyHint="next"
                  aria-invalid={statusTone === "error" && !typedName.trim()}
                  className="min-h-14 w-full rounded-[1.25rem] border border-[#d9cfb3] bg-[#fffdfa] px-4 py-3 text-base outline-none focus:border-[#caa23a] disabled:cursor-not-allowed disabled:opacity-70"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-[#1f3f6d]">
                  Optional signature
                </label>
                <GuestbookSignaturePad
                  value={signatureDataUrl}
                  onChange={setSignatureDataUrl}
                  disabled={formDisabled}
                />
              </div>

              {statusText ? (
                <div
                  role={statusTone === "error" ? "alert" : "status"}
                  className={statusTone === "error"
                    ? "rounded-[1.25rem] border border-[#d86f62]/40 bg-[#fff7f5] px-4 py-3 text-sm text-[#7a3029]"
                    : "rounded-[1.25rem] border border-[#decf9b] bg-[#f8f5ed] px-4 py-3 text-sm text-[#1f4e79]"
                  }
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-3">
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#1f4e79]/20 border-t-[#1f4e79]" aria-hidden="true" />
                      {statusText}
                    </span>
                  ) : (
                    statusText
                  )}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={formDisabled}
                className="inline-flex min-h-14 w-full items-center justify-center gap-3 rounded-full bg-[#1f4e79] px-6 py-3 text-sm font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                    Submitting your message...
                  </>
                ) : (
                  "Add to Guestbook"
                )}
              </button>
            </form>
          </section>

          <section className="rounded-[1.75rem] border border-[#decf9b] bg-[linear-gradient(180deg,#fffdfa_0%,#f6efe2_100%)] px-6 py-7 shadow-sm">
            <p className="text-[11px] uppercase tracking-[.24em] text-[#8c8c86]">
              Live Preview
            </p>
            <h2 className="mt-3 text-4xl font-semibold text-[#1f3f6d]">
              Messages from your guests
            </h2>
            <p className="mt-4 text-base leading-8 text-[#686861]">
              Messages signed here also appear in the couple&apos;s private dashboard view.
            </p>
          </section>
        </div>

        <section ref={entriesSectionRef} className="mt-10 scroll-mt-6">
          <GuestbookCards
            entries={entries}
            highlightedEntryId={highlightedEntryId}
            emptyMessage={isLoadingEntries
              ? "Loading guestbook messages..."
              : "No messages have been written yet. Be the first to sign the guestbook."
            }
          />
        </section>
      </div>

      {showSuccessDialog ? (
        <div className="guestbook-success-backdrop fixed inset-0 z-50 flex items-center justify-center bg-[#10233d]/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="guestbook-success-title"
            className="guestbook-success-card w-full max-w-lg rounded-[2rem] border border-[#decf9b] bg-[linear-gradient(180deg,#fffdfa_0%,#f5eee0_100%)] p-6 text-center shadow-[0_24px_70px_rgba(31,78,121,0.22)] sm:p-8"
          >
            <p className="text-4xl" aria-hidden="true">❤️</p>
            <h2
              id="guestbook-success-title"
              className={`${greatVibes.className} wedding-gold-halo mt-3 text-6xl text-[#1f4e79]`}
            >
              Thank You!
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#1f3f6d]">
              Your message has been safely added to John &amp; Maxine&apos;s Wedding Guestbook.
            </p>
            <p className="mt-4 text-base leading-8 text-[#686861]">
              They&apos;ll be able to read all of your wonderful messages after the wedding.
            </p>
            <p className="mt-4 text-base leading-8 text-[#686861]">
              Thank you for celebrating with us!
            </p>
            <p className="mt-4 text-3xl" aria-hidden="true">❤️</p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={closeSuccessDialog}
                className="inline-flex min-h-13 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[.12em] text-[#1f4e79] transition hover:bg-[#f8f5ed]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={closeSuccessDialog}
                className="inline-flex min-h-13 items-center justify-center rounded-full bg-[#1f4e79] px-6 py-3 text-sm font-semibold uppercase tracking-[.12em] text-white transition hover:bg-[#173d61]"
              >
                View My Message
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
